import React, { useState, useRef, useEffect } from "react";
import { FaArrowLeft, FaSpinner, FaVideo } from "react-icons/fa";
import { uploadVideoToCloudinary } from "../../../utils/cloudinary";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import axiosInstance from "../../../utils/axios";
import { useNavigate } from "react-router-dom";

const UploadProgressOverlay = ({ progress }) => {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center z-[9999]">
      {/* Blocking overlay */}
      <div className="absolute inset-0 bg-black/30 rounded-lg pointer-events-auto" />

      {/* Actual progress UI */}
      <div className="relative flex flex-col items-center justify-center gap-4">
        <FaSpinner className="text-5xl text-white animate-spin mb-2" />
        <p className="text-white font-semibold">Uploading: {progress}%</p>
        <div className="w-48 h-2 bg-gray-300 rounded-full overflow-hidden">
          <div
            className="h-2 bg-blue-500 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const AdminCreateTestimonials = () => {
  const [title, setTitle] = useState("");
  const [sequence, setSequence] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");
  const navigate = useNavigate();

  const videoInputRef = useRef(null);

  const triggerVideoInput = () => videoInputRef.current.click();

  // Generate local preview when file is selected
  useEffect(() => {
    if (videoFile) {
      const previewUrl = URL.createObjectURL(videoFile);
      setVideoPreview(previewUrl);

      // Clean up object URL to avoid memory leaks
      return () => URL.revokeObjectURL(previewUrl);
    }
  }, [videoFile]);

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      setVideoFile(e.target.files[0]);
      setVideoUrl(""); // Reset any previously uploaded video
      setProgress(0);
    }
  };

  const handleCancelUpload = () => {
    setVideoFile(null);
    setVideoUrl("");
    setVideoPreview("");
    setProgress(0);
    setUploading(false);
    // Reset file input so the same file can be selected again
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!videoFile) return toast.error("Please select a video!");
    try {
      setUploading(true);
      const secure_url = await uploadVideoToCloudinary(videoFile, (p) =>
        setProgress(p)
      );
      setVideoUrl(secure_url);
      setUploading(false);
      setVideoPreview(""); // replace local preview with uploaded video
      setProgress(0);
    } catch (err) {
      console.error(err);
      setUploading(false);
      setProgress(0);
      toast.error(err.message || "Upload failed!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!videoUrl) return toast.error("video is required!");

    try {
      await axiosInstance.post("/testimonials", {
        title,
        sequence,
        videoUrl,
      });

      Swal.fire({
        icon: "success",
        title: "Testimonial created successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      // Reset form
      setTitle("");
      setSequence(null);
      handleCancelUpload();
    } catch (err) {
      console.error(err);
      toast.error("Error creating testimonial");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-12 h-12 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
          >
            <FaArrowLeft className="text-gray-600 group-hover:text-gray-800 transition-colors duration-200" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Create Testimonial
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <form
          className={uploading ? "pointer-events-none opacity-50" : ""}
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Form Fields */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-100 pb-4">
                Video Upload
              </h2>

              {/* Video Upload Section */}
              <div className="relative">
                <div className="mb-6">
                  <label className="block font-medium mb-3 text-gray-700">
                    Testimonial Video *
                  </label>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <div className="text-sm">
                        <p className="text-amber-800 font-medium mb-1">
                          Maximum file size:{" "}
                          {process.env.REACT_APP_TESTIMONIAL_VIDEO_SIZE || 25}{" "}
                          MB
                        </p>
                        <p className="text-amber-700">
                          Recommended: Upload portrait-oriented videos (9:16
                          ratio) for best mobile experience
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upload / Preview Container - Portrait Format */}
                <div className="flex justify-center">
                  {!videoUrl && !videoPreview && (
                    <div className="relative flex flex-col items-center justify-center w-80 h-[480px] border-2 border-dashed border-gray-300 rounded-2xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 bg-gradient-to-br from-gray-50 to-gray-100">
                      <button
                        type="button"
                        onClick={triggerVideoInput}
                        className="flex flex-col items-center justify-center w-full h-full group"
                      >
                        {uploading ? (
                          <FaSpinner className="text-6xl text-blue-400 animate-spin mb-4" />
                        ) : (
                          <>
                            <div className="bg-blue-100 p-6 rounded-full mb-6 group-hover:bg-blue-200 transition-colors duration-200">
                              <FaVideo className="text-4xl text-blue-600" />
                            </div>
                            <span className="text-xl text-gray-600 text-center font-medium mb-2">
                              {videoFile ? "Ready to Upload" : "Upload Video"}
                            </span>
                            <span className="text-sm text-gray-500 text-center px-8">
                              Click here to select your testimonial video
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* File input */}
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {/* Video Preview - Portrait Format */}
                  {(videoPreview || videoUrl) && (
                    <div className="relative w-80">
                      {uploading && (
                        <UploadProgressOverlay progress={progress} />
                      )}
                      <div className="relative group">
                        <div className=" bg-black rounded-2xl overflow-hidden shadow-2xl">
                          <video
                            src={videoUrl || videoPreview}
                            controls
                            className="w-full h-[480px] object-cover"
                          />
                        </div>
                        {!videoUrl && (
                          <button
                            type="button"
                            onClick={handleCancelUpload}
                            className="absolute -top-2 z-50 -right-2 bg-red-500 hover:bg-red-600 opacity-0 group-hover:opacity-100 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg transition-all duration-300 text-sm font-bold"
                            title="Remove video"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {videoUrl && (
                        <div className="mt-4 text-center">
                          <span className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                            Video uploaded successfully
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Video Upload */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-100 pb-4">
                  Testimonial Details
                </h2>

                {/* Title */}
                <div className="mb-8">
                  <label className="block text-gray-700 font-medium mb-3">
                    Title{" "}
                    <span className=" text-sm text-amber-600 font-normal mt-1">
                      (Optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                    placeholder="Enter testimonial title"
                  />
                </div>

                {/* Sequence */}
                <div>
                  <label className="block text-gray-700 font-medium mb-3">
                    Display Sequence
                    <span className="block text-sm text-amber-600 font-normal mt-1">
                      Optional - Controls the order of testimonials
                    </span>
                  </label>
                  <input
                    type="number"
                    value={sequence}
                    placeholder="0"
                    min={0}
                    onChange={(e) => setSequence(Number(e.target.value))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Tip: Use arrow keys to adjust the value
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                {/* Upload Button (only shows when ready to upload) */}
                {videoFile && !videoUrl && !uploading && (
                  <button
                    type="button"
                    onClick={handleUpload}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-3 text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <FaVideo className="text-xl" /> Upload Video
                  </button>
                )}

                {/* Save Button */}
                {videoUrl && !uploading && (
                  <button
                    type="submit"
                    disabled={!videoUrl}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-semibold disabled:opacity-50 text-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Save Testimonial
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCreateTestimonials;
